import {Component, OnDestroy, OnInit} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule, ValidatorFn,
  Validators
} from "@angular/forms";
import {ApiService, ChatbotDef, RagTemplateDef} from "../../../services/api.service";
import {AuthService} from "../../../auth/services/auth.service";
import {ActivatedRoute, Router} from "@angular/router";
import {JsonPipe, Location, NgClass, NgForOf, NgIf} from "@angular/common"
import {QuickPreviewPanelComponent} from "../../../core/quick-preview-panel/quick-preview-panel.component";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {ActionsContainerComponent} from "../../../core/actions-container/actions-container.component";
import {BackButtonComponent} from "../../../core/back-button/back-button.component";
import {MatIconModule} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {ErrorPanelComponent} from "../../../core/error-panel/error-panel.component";
import {MatProgressBarModule} from "@angular/material/progress-bar";
import {MatSelectModule} from "@angular/material/select";
import {catchError, map, of, Subscription, throwError} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";
import {TextareaHighlightComponent} from "../../../core/textarea-highlight/textarea-highlight.component";
import {StateService} from "../../../services/state.service";


export function templateUniqueNameValidator(api: ApiService, existingTemplateId?: string): AsyncValidatorFn {
  return (control: AbstractControl) => {
    return api.filterTemplates({ filterName: control.value?.trim() }).pipe(
      catchError((e: HttpErrorResponse) =>
        e.status == 404
          ? of (undefined)
          : throwError(() => e)),
      map(matches => {
        if (!matches) return null
        if (!matches.length) return null
        return !existingTemplateId || matches.filter(m => m.id != existingTemplateId).length ?
          {
            exists: true
          } : null
      })
    )
  }
}


@Component({
  selector: 'templating-manage',
  standalone: true,
  imports: [
    NgIf,
    QuickPreviewPanelComponent,
    ReactiveFormsModule,
    NgClass,
    MatFormFieldModule,
    MatInputModule,
    ActionsContainerComponent,
    BackButtonComponent,
    MatIconModule,
    MatButtonModule,
    ErrorPanelComponent,
    MatProgressBarModule,
    JsonPipe,
    NgForOf,
    MatSelectModule,
    TextareaHighlightComponent
  ],
  templateUrl: './templating-manage.component.html',
})
export class TemplatingManageComponent implements OnInit, OnDestroy {

  readonly messageTypes = ['SYSTEM', 'USER']
  readonly messageChanges: {[id: number | string]: Subscription} = {}
  readonly messageWarnings: {[id: number | string]: string } = {}
  readonly messageVars: {[id: number | string]: string[] } = {}
  chatbots: ChatbotDef[] = []
  currentChatbotUid?: string
  availableContextVars: string[] = []
  queryParamsSub?: Subscription

  form?: FormGroup
  formChanges?: Subscription
  templateId?: string
  existing?: RagTemplateDef
  loading = false
  error?: any
  roleCheckFailed = false
  userIsCreator = true
  cloningFromSource = false

  get isNew(): boolean {
    return !this.templateId
  }

  get canManage() {
    return this.isNew || !this.roleCheckFailed || this.userIsCreator
  }

  get messagesArrayControl(): FormArray {
    return this.form!.get('messages') as FormArray
  }

  get messagesArrayControls(): FormGroup[] {
    return this.messagesArrayControl.controls as FormGroup[]
  }

  get formAsTemplateDef(): RagTemplateDef {
    const result =  (this.form!.value as RagTemplateDef)!
    if (!result.id && this.templateId) {
      result.id = this.templateId
    }
    return result
  }

  constructor(
    readonly api: ApiService,
    readonly stateService: StateService,
    readonly authService: AuthService,
    readonly activatedRoute: ActivatedRoute,
    readonly fb: FormBuilder,
    readonly router: Router,
    readonly location: Location
  ) {
    this.roleCheckFailed = !this.authService.hasRoleInRange(['Super', 'Admin'])
  }

  ngOnInit(): void {
    this.api.getChatbots().subscribe({
      next:(cb) => {
        this.chatbots = cb
        if (this.chatbots.length) {
          this.currentChatbotUid = cb[0].id
          this.availableContextVars = cb[0].contextVars
          this.stateService.contextVars = this.availableContextVars
        }
        this.templateId = this.activatedRoute.snapshot.params['templateId']
        this.invalidateForm()
        this.queryParamsSub = this.activatedRoute.queryParams.subscribe({
          next:((qp) => {
            if (!this.templateId && qp['source']) {
              this.loading = true
              this.userIsCreator = true
              this.cloningFromSource = true
              this.api.getTemplate(qp['source']).subscribe({
                next: (sourceTemplate) => {
                  (sourceTemplate.messages || []).forEach(() => this.addMessageControl())
                  sourceTemplate.name = `${sourceTemplate.name} copy (${Date.now()})`
                  this.form?.patchValue(sourceTemplate, {
                    emitEvent: true
                  })
                  this.loading = false
                },
                error: (e) => {
                  this.error = e
                  this.loading = false
                  this.cloningFromSource = false
                }
              })
            }
          })
        })
      }
    })
  }

  ngOnDestroy() {
    this.queryParamsSub?.unsubscribe()
    this.queryParamsSub = undefined
    this.formChanges?.unsubscribe()
    this.formChanges = undefined

    Object.getOwnPropertyNames(this.messageChanges).forEach((key) => {
      console.warn('Cleaning subscription for', key)
      this.messageChanges[key].unsubscribe()
      delete this.messageChanges[key]
    })
  }

  castToFormControl(c: AbstractControl) {
    return c as FormControl
  }

  deleteControlAtIndex(index: number) {
    this.messagesArrayControl.removeAt(index, {
      emitEvent: true
    })
    delete this.messageChanges[index]
  }

  invalidateForm(): void {
    this.form = this.fb.group({
      isPendingNew: [this.templateId === undefined],
      id: new FormControl({
        value: this.templateId || '',
        disabled: false
      }),
      assistantId: [this.chatbots.length ? this.chatbots[0].id : null, Validators.required],
      name: new FormControl({
        value: '', disabled: false
      }, [Validators.required],
        [templateUniqueNameValidator(this.api, this.templateId)]
      ),
      description: [''],
      messages: this.fb.array([])
    })
    this.userIsCreator = true
    if (this.templateId) {
      this.loading = true
      this.userIsCreator = false
      this.api.getTemplate(this.templateId).subscribe({
        next: (template) => {
          (template.messages || []).forEach(() => this.addMessageControl())
          this.form?.patchValue(template, {
            emitEvent: true
          })
          this.existing = template
          this.loading = false
          this.userIsCreator = template.createdBy == this.authService.getSessionUserId()
        },
        error: (e) => {
          this.error = e
          this.loading = false
        }
      })
    }

    console.warn('change form value', this.form.value['assistantId'])

    this.formChanges = this.form.valueChanges.subscribe({
      next: (value) => {
        if (value['assistantId'] && value['assistantId'] != this.currentChatbotUid) {
          this.currentChatbotUid = value['assistantId']
          const matchingDb = this.chatbots.find(
            (cb) => cb.id == this.currentChatbotUid)
          console.warn('reload', matchingDb?.contextVars )
          this.availableContextVars = matchingDb?.contextVars || []
          this.stateService.contextVars = this.availableContextVars
          this.messagesArrayControl.controls.forEach((fg: any, index) => {
            const message = fg.controls['message'].value
            this.invalidateWarningsForVars(index, message)
          })
        }
      }
    })
  }

  save(): void {
    if (this.form?.valid) {
      const templateDef = this.formAsTemplateDef
      const createOrUpdate = this.isNew
        ? this.api.createTemplate(templateDef)
        : this.api.updateTemplate(templateDef)
      this.error = undefined
      this.loading = true
      createOrUpdate.subscribe({
        next: (_) => {
          this.loading = false
          this.location.back()
        },
        error: (e) => {
          this.loading = false
          this.error = e
        },
      })
    }
  }

  addMessageControl() {
    const control = this.fb.group({
      'role': [this.messageTypes[0], Validators.required],
      'message': new FormControl('', {
        validators: [Validators.required]
      })
    })
    const index = this.messagesArrayControl.controls.length
    this.messagesArrayControl.push(control, { emitEvent: true })
    this.messageChanges[index] = control.valueChanges.subscribe({
      next: (v: any) => {
        this.invalidateWarningsForVars(index, v.message)
      }
    })
  }

  invalidateWarningsForVars(index: number, message: string) {

    this.messageVars[index] = []
    const varTemplateRegex = /\{\{(.*?)}}/gm;
    let match = varTemplateRegex.exec(message)
    if (index in this.messageWarnings) {
      delete this.messageWarnings[index]
    }
    if (match) {
      if (!this.availableContextVars.includes(match[1])) {
        console.warn('match', match[1], this.availableContextVars)
        this.messageWarnings[index] = `Unknown var "${match[1]}"`
      }
      else {
        this.messageVars[index].push(match[1])
      }
    }
  }
}
